import Analytics from '@opencare/analytics';
import { PopupButton as TypeformPopupButton } from '@typeform/embed-react';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';

import { LoadingSpinner } from 'brand-guidelines/Loading-styled-components';
import { COUNTRY } from 'common/constants';
import { getInsuranceList, getInsuranceListForCanada, requestClinic, updateBookingWidgetSlot, updateClinicLoading } from 'connect/actions';
import { ReceivedOffers } from 'connect/saga/handlers/offer/received-offers';
import { UpdatePatientSilently } from 'connect/saga/handlers/update-patient-silently/update-patient-silently';
import { selectDataClinicLoading, selectDataMatchId, selectDataPatient, selectDataUserAuthenticated } from 'connect/selectors';
import { DROPOFF_SURVEY_TYPEFORM_ID } from 'connect/state/handlers/typeform-modal';
import { CurrentStep } from 'pages/checkout/components/StepManager/CurrentStep';
import { SSO_REDIRECT_SEARCH_PARAM } from 'pages/checkout/constants';
import { useCurrentCheckoutStep } from 'pages/checkout/hooks/checkoutStepHooks';
import { StepId } from 'pages/checkout/steps/types';
import { BookingRestrictedModal } from 'pages/profile/components/BookingRestrictedModal/BookingRestrictedModal';
import { selectDataClinic } from 'pages/profile/connect/selectors';

import { useTemporaryQueryParams } from 'utils/customHooks/use-query-params';
import { DropoffSurveySource, useDropoffSurvey } from 'utils/customHooks/useDropoffSurvey';

import { CheckoutGlobalStyle, CheckoutPageWrapper, CheckoutStepWrapper, TopSection } from './CheckoutPage-styled-components';
import { Navbar } from './components/Navbar/Navbar';
import { OfferBanner } from './components/OfferBanner/OfferBanner';
import { Progress } from './components/Progress/Progress';
import { resetCheckoutData } from './connect/helpers';
import { selectInsuranceListForClinicCountry } from './connect/legacy-selectors';

const analytics = new Analytics();

export const CheckoutPage = () => {
  const dispatch = useDispatch();
  const { params } = useRouteMatch();

  // Track whether the clinic has loaded and don't show the spinner if it has.
  // We need to do this instead of relying on the clinicLoading state so that
  // checkout pages can refresh the same clinic data.
  // If we don't do this, the page will not render while the loading spinner shows
  // and states will be reset.
  const [hasLoadedClinic, setHasLoadedClinic] = useState(false);

  const isUserAuthenticated = useSelector(selectDataUserAuthenticated);
  const patient = useSelector(selectDataPatient);

  const { clinicId: clinicIdString } = params as { clinicId: string; };
  const clinicId = parseInt(clinicIdString, 10);

  const ssoQueryParams = useTemporaryQueryParams([
    { variableName: 'ssoRedirect', paramKey: SSO_REDIRECT_SEARCH_PARAM },
    { variableName: 'isNewUser', paramKey: 'isNewUser' },
  ]);

  const isNewSsoUser = useMemo(
    () => ssoQueryParams.ssoRedirect && ssoQueryParams.isNewUser,
    [ssoQueryParams],
  );

  // Needed for the dropoff survey
  const matchId = useSelector(selectDataMatchId);
  const { currentStep } = useCurrentCheckoutStep();
  const isOnVerifyStep = currentStep?.id === StepId.VERIFY;
  const { typeformRef, hiddenFields } = useDropoffSurvey({
    source: DropoffSurveySource.Checkout,
    checkoutStep: currentStep?.id,
    disabled: isOnVerifyStep,
  });

  // Reset all saga loading and error states on first load
  // This makes sure we don't have any stale data from previous visits
  useEffect(() => {
    resetCheckoutData(dispatch);

    // Reset booking widget slot to acknowledge its been handled
    dispatch(updateBookingWidgetSlot(null, null, null));

    // Reset clinic loading to false to clear any stale loading state
    dispatch(updateClinicLoading(false));
  }, [dispatch]);

  // Record when the patient first enters checkout (idempotent — only written once)
  useEffect(() => {
    if (!patient?.id || patient?.startedCheckoutAt) {
      return;
    }
    dispatch(
      UpdatePatientSilently.createAction({
        patientId: patient.id,
        startedCheckoutAt: moment.utc().toISOString(),
      }),
    );
  }, [patient, dispatch]);

  const clinicData = useSelector(selectDataClinic);
  const clinicLoading = useSelector(selectDataClinicLoading);

  useEffect(() => {
    if (clinicLoading) {
      return;
    }

    if (clinicData?.id === clinicId) {
      setHasLoadedClinic(true);
      return;
    }

    // Either clinicData is not loaded or clinicId has changed
    dispatch(requestClinic(clinicId));
    setHasLoadedClinic(false);
  }, [clinicId, clinicData, clinicLoading, dispatch]);

  // Fetch insurance list
  const insuranceList = useSelector(selectInsuranceListForClinicCountry);
  useEffect(() => {
    if (insuranceList?.length) {
      return;
    }

    if (clinicData?.address?.country === COUNTRY.canada) {
      dispatch(getInsuranceListForCanada());
      return;
    }

    dispatch(getInsuranceList());
  }, [insuranceList, dispatch, clinicData?.address?.country]);

  // Fetch received offers
  const receivedOffers = useSelector(ReceivedOffers.value!.selector);
  useEffect(() => {
    if (Array.isArray(receivedOffers) || !isUserAuthenticated) {
      return;
    }

    dispatch(ReceivedOffers.createAction());
  }, [dispatch, receivedOffers, isUserAuthenticated]);

  useEffect(() => {
    if (!isNewSsoUser || !isUserAuthenticated) {
      return;
    }

    analytics.trackSignUp({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'checkout-page',
      name: 'checkout',
      section: 'sso-redirect',
    });
  }, [isNewSsoUser, isUserAuthenticated]);

  // We're loading if the clinic in redux is not the same as the clinicId in the URL
  // Or if the clinic saga handler is still loading
  const isLoading = clinicData?.id !== clinicId || !hasLoadedClinic;
  return (
    <>
      <CheckoutGlobalStyle />

      <CheckoutPageWrapper>
        <TopSection>
          <Navbar />
          <Progress />
        </TopSection>

        <CheckoutStepWrapper>
          <OfferBanner />
          {isLoading ? <LoadingSpinner /> : <CurrentStep />}
        </CheckoutStepWrapper>
      </CheckoutPageWrapper>

      <TypeformPopupButton
        id={DROPOFF_SURVEY_TYPEFORM_ID}
        embedRef={typeformRef}
        preventReopenOnClose={true}
        hidden={{ match_id: matchId, ...hiddenFields }}
        style={{ display: 'none' }}
        respectOpenModals="all"
        autoClose={1000}
      />
      <BookingRestrictedModal />
    </>
  );
};
